import style from './AssetPreview.module.css';
import MainHeading from "../../shared_components/MainHeading/MainHeading";
import WhiteMainBackground from "../../shared_components/WhiteMainBackground/WhiteMainBackground";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BackgroundButtons from './BackgroundButtons/BackgroundButtons';
import { useContext, useEffect, useRef, useState } from 'react';
import Utils from '../../utils/Utils';
import Spinner from '../../shared_components/Spinner/Spinner';
import PluginAssetService from '../../services/PluginAssetService';
import UserData from '../../services/UserData';
import InfoPage from '../InfoPage/InfoPage';
import ENV from '../../services/Environment';
import AssetViewWithTracking from '../../shared_components/AssetView/AssetViewWithTracking';

const AssetPreviewContent = () => {
  const location = useLocation();
  const userData = useContext(UserData);
  const { assetName, assetKey, assetType } = useParams();

  const referrer = Utils.getFromLocationOrDefault('referrer', location, `/plugins/asset/interactive/${assetKey}`);
  const urlFromLocation = Utils.getFromLocationOrDefault('url', location, null);

  const navigate = useNavigate();
  const [url, updateUrl] = useState(null);
  const sessionRef = useRef({ active: false, request: null, deleted: false });
  const [hasError, updateHasError] = useState(false);
  const [errorMessage, updateErrorMessage] = useState({});
  const [backgroundStyle, updateBackgroundStyle] = useState(ENV.appIconName);

  const backButtonAction = () => {
    navigate(referrer);
  }

  const changeBackground = (selectedStyle) => {
    updateBackgroundStyle(selectedStyle);
  }

  const getIframeView = () => {
    return Utils.isNull(url) ?
      <Spinner /> : 
      <AssetViewWithTracking styleName={backgroundStyle} url={url.location} title={assetName} />;
  }

  useEffect(() => {
    const session = sessionRef.current;
    session.active = true;
    // Reuse the pending request during React StrictMode's effect replay.
    if (!session.request) {
      session.request = Utils.isNull(urlFromLocation)
        ? PluginAssetService.previewAsset(userData, assetKey, assetName, assetType)
        : Promise.resolve(urlFromLocation);
    }
    session.request.then(data => {
      if (!session.active) return;
      const previewLocation = typeof data === 'string' ? data : data?.location;
      if (data?.ok === false || typeof previewLocation !== 'string' || !previewLocation.trim()) {
        throw new Error('The preview service did not return an asset location. Please reopen the asset.');
      }
      updateUrl({ location: previewLocation });
    }).catch(error => {
      if (!session.active) return;
      updateHasError(true);
      updateErrorMessage({ reason: 'Asset not found', description: error.message || Utils.getUsefulError(error) });
    });

    const deletePreview = () => {
      if (session.active || session.deleted) return;
      session.deleted = true;
      PluginAssetService.deletePreview(userData, assetName).catch(console.error);
    };

    return () => {
      session.active = false;
      // StrictMode immediately reactivates this session. On an actual departure,
      // wait for creation to finish before deleting the preview it created.
      Promise.resolve().then(() => {
        if (!session.active) session.request.then(deletePreview, deletePreview);
      });
    };
  }, [urlFromLocation, assetKey, userData, assetName, assetType]);

  const iframeView = getIframeView();

  const getPreview = () => {
    if (hasError) {
      return <InfoPage reason={errorMessage.reason} description={errorMessage.description} />
    }

    return (
      <>
        {iframeView}
        <BackgroundButtons changeBackground={changeBackground}/>
      </>
    )
  }

  const assetView = getPreview();

  return (
    <WhiteMainBackground>
      <div className={style.main}>
        <MainHeading backButtonAction={backButtonAction}>{assetName}</MainHeading>
        {assetView}
      </div>
    </WhiteMainBackground>
  )
}

const AssetPreview = () => {
  const { assetName, assetKey, assetType } = useParams();
  return <AssetPreviewContent key={JSON.stringify([assetType, assetKey, assetName])} />;
};

export default AssetPreview;
